<script>
  import _ from 'lodash'
  import Draggable from 'vuedraggable'
  import VActionBar from './v-action-bar'

  export default {
    name: 'VGridformBuilderSort',
    components: {VActionBar, Draggable},
    props: {
      value: {
        type: Boolean,
        default: false
      },
      config: {
        type: Array,
        default: () => ([])
      }
    },
    data() {
      return {
        open: false,
        lists: [],
        listMap: {},
        colMap: {},
        drawer: true
      }
    },

    watch: {
      value(open) {
        this.open = open
      },
      config() {
        this.setLists()
      }
    },

    mounted() {
      this.open = this.value
      this.drawer = true
      this.setLists()
    },

    methods: {
      setLists() {
        if (!Array.isArray(this.config)) return

        const lists = []
        const colMap = {}
        const listMap = {}

        this.config.forEach(list => {
          listMap[list.fieldlist.id] = list.fieldlist
          const elements = list.rows.map(col => {
            colMap[col[0].name] = col[0]
            return {
              id: col[0].name,
              name: col[0].label
            }
          })
          const {id, name} = list.fieldlist
          lists.push({
            id,
            name,
            elements,
            collapsed: false
          })
        })

        this.colMap = colMap
        this.listMap = listMap
        this.lists = lists
      },

      itemClasses(fl) {
        return `my-1 px-2 ${fl.collapsed ? 'collapsed' : ''}`.trim()
      },

      prefixActions(fl) {
        return [
          {
            name: 'expand',
            icon: 'mdi-plus-box-outline',
            label: this.i18n('sort.expand'),
            hidden: !fl.collapsed
          },
          {
            name: 'collapse',
            icon: 'mdi-minus-box-outline',
            label: this.i18n('sort.collapse'),
            hidden: fl.collapsed
          }
        ]
      },
      actions(fl, idx) {
        const lastIdx = this.lists.length
        return [
          {
            name: 'sort-alpha-asc',
            icon: 'mdi-sort-alphabetical-ascending'
          },
          {
            name: 'sort-alpha-desc',
            icon: 'mdi-sort-alphabetical-descending'
          },
          { divider: true },
          {
            name: 'first',
            icon: 'mdi-chevron-double-up',
            label: this.i18n('sort.first'),
            disabled: idx === 0
          },
          {
            name: 'up',
            icon: 'mdi-chevron-up',
            label: this.i18n('sort.prev'),
            disabled: idx === 0
          },
          {
            name: 'down',
            icon: 'mdi-chevron-down',
            label: this.i18n('sort.next'),
            disabled: idx === lastIdx - 1
          },
          {
            name: 'last',
            icon: 'mdi-chevron-double-down',
            label: this.i18n('sort.last'),
            disabled: idx === lastIdx - 1
          }
        ]
      },

      onAction(action, fl, idx) {
        if (action.name === 'sort-alpha-asc') {
          fl.elements = _.orderBy(fl.elements, ['name'], ['asc'])
        }
        else if (action.name === 'sort-alpha-desc') {
          fl.elements = _.orderBy(fl.elements, ['name'], ['desc'])
        }
        else if (action.name === 'collapse') fl.collapsed = true
        else if (action.name === 'expand') fl.collapsed = false
        else {
          console.log('action', action.name, action)
          console.log('fl, idx', fl, idx)
        }
      },

      collapseAll() {
        this.lists.forEach(list => {list.collapsed = true})
      },
      expandAll() {
        this.lists.forEach(list => {list.collapsed = false})
      },

      // rebuild rows configuration from submitted sorting
      submit() {
        const rows = []
        const lists = _.cloneDeep(this.lists)
        _.each(lists, list => {
          // get fieldlist data
          const fieldlist = this.listMap[list.id]
          _.each(list.elements, col => {
            // set new fieldlist to column
            const row = Object.assign({}, this.colMap[col.id], {fieldlist})
            rows.push([row])
          })
        })
        this.$emit('submit', rows)
      },
      close() {
        this.$emit('close')
      },

      i18n(key) {
        return this.$vuetify.lang.t(`$vuetify.gfb.${key}`)
      }
    }
  }
</script>

<template>
  <VDialog v-model="open" fullscreen persistent>
    <!-- Nested draggables -->
    <VSheet class="vgfbs">
      <Draggable :list="lists" draggable=".vgfbs-list" group="lists" :force-fallback="true">
        <div v-for="(fl, idx) in lists" :key="idx" class="vgfbs-list">
          <Draggable :list="fl.elements" draggable=".vgfbs-list__item" group="items" :force-fallback="true">
            <VSheet v-for="(element, index) in fl.elements" :key="element.id" height="24" class="vgfbs-list__item" :class="itemClasses(fl)">
              <VRow class="ma-0">
                <VCol cols="11" class="px-2 py-0">{{ element.name }}</VCol>
                <VCol cols="1" class="px-2 py-0 text-end">{{ index + 1 }}</VCol>
              </VRow>
            </VSheet>

            <!-- Header slot: Actionbar -->
            <VSheet slot="header" class="px-0" color="accent">
              <VActionBar :title="fl.name" :prefix-actions="prefixActions(fl)" :actions="actions(fl, idx)" @click="onAction($event, fl, idx)"/>
            </VSheet>
          </Draggable>
        </div>
      </Draggable>
    </VSheet>

    <!-- Action buttons: Cancel, Apply -->
    <VFooter fixed>
      <VSpacer/>
      <VBtn text @click="close">{{ $vuetify.lang.t('$vuetify.actions.cancel') }}</VBtn>
      <VBtn text @click="submit">{{ $vuetify.lang.t('$vuetify.actions.apply') }}</VBtn>
    </VFooter>

    <!-- Sidebar -->
    <VNavigationDrawer v-if="!$vuetify.breakpoint.smAndDown" :value="drawer" mini-variant clipped permanent>
      <VList dense>
        <VTooltip right>
          <template v-slot:activator="{ on }">
            <VListItem @click.left="expandAll">
              <VListItemIcon>
                <VIcon v-on="on">mdi-expand-all-outline</VIcon>
              </VListItemIcon>
              <VListItemContent/>
            </VListItem>
          </template>
          <span>{{ i18n('expandHint') }}</span>
        </VTooltip>
        <VTooltip right>
          <template v-slot:activator="{ on }">
            <VListItem @click.left="collapseAll">
              <VListItemIcon>
                <VIcon v-on="on">mdi-collapse-all-outline</VIcon>
              </VListItemIcon>
              <VListItemContent/>
            </VListItem>
          </template>
          <span>{{ i18n('collapseHint') }}</span>
        </VTooltip>
      </VList>
    </VNavigationDrawer>
  </VDialog>
</template>

<style lang="scss">
  .vgfbs {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 48px;
    left: 0;
    overflow: auto;
  }
  .vgfbs-list {
    max-width: 640px;
    padding: 0 8px;
    margin: 16px auto;
    cursor: move;
  }
  .vgfbs-list__item {
    cursor: move;
    border: 1px dashed #AAA !important;
    &.collapsed {
      display: none;
    }
  }
</style>
